import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment/min/moment-with-locales";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Card, Typography } from "@mui/material";

import MDBox from "../../components/MDBox";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../layouts/DashboardLayout";
import EventDialog from "../../components/Calendar/EventDialog";
import { usersAPI } from "../../services/api";
import { calendarAPI } from "../../services/api";

moment.locale("de");

const localizer = momentLocalizer(moment);

const messages = {
  allDay: "Ganztägig",
  previous: "Zurück",
  next: "Weiter",
  today: "Heute",
  month: "Monat",
  week: "Woche",
  day: "Tag",
  agenda: "Agenda",
  date: "Datum",
  time: "Zeit",
  event: "Termin",
  noEventsInRange: "Keine Termine in diesem Zeitraum.",
};

const formats = {
  monthHeaderFormat: (date, culture, localizer) =>
    moment(date).locale("de").format("MMMM YYYY"),
  dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
    moment(start).locale("de").format("DD. MMMM") +
    " - " +
    moment(end).locale("de").format("DD. MMMM YYYY"),
  dayHeaderFormat: (date, culture, localizer) =>
    moment(date).locale("de").format("dddd, DD. MMMM"),
  weekdayFormat: (date, culture, localizer) =>
    moment(date).locale("de").format("dd"),
};

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [fetchedMonths, setFetchedMonths] = useState(new Set());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const location = useLocation();

  const getMonthKey = (date) => moment(date).format("YYYY-MM");

  const checkAndFetchMonths = async (date, currentFetchedSet) => {
    const monthsNeeded = [
      moment(date).subtract(1, "months"),
      moment(date),
      moment(date).add(1, "months"),
    ];

    const missingMonths = monthsNeeded.filter(
      (m) => !currentFetchedSet.has(getMonthKey(m.toDate())),
    );

    if (missingMonths.length === 0) return;

    const startDates = missingMonths.map((m) => m.clone().startOf("month"));
    const endDates = missingMonths.map((m) => m.clone().endOf("month"));

    const minDate = moment.min(startDates);
    const maxDate = moment.max(endDates);

    try {
      const response = await calendarAPI.getCalendarEvents({
        start_date: minDate.toISOString(),
        end_date: maxDate.toISOString(),
      });

      const fetchedEvents = response.data.map((event) => ({
        ...event,
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        allDay: event.all_day,
      }));

      setEvents((prevEvents) => {
        const existingIds = new Set(prevEvents.map((e) => e.id));
        const uniqueNewEvents = fetchedEvents.filter(
          (e) => !existingIds.has(e.id),
        );
        const updatedEvents = [...prevEvents, ...uniqueNewEvents];

        localStorage.setItem("calendarEvents", JSON.stringify(updatedEvents));
        localStorage.setItem("calendarEvents_timestamp", Date.now().toString());
        return updatedEvents;
      });

      setFetchedMonths((prevSet) => {
        const newSet = new Set(prevSet);
        missingMonths.forEach((m) => newSet.add(getMonthKey(m.toDate())));
        localStorage.setItem("fetchedMonths", JSON.stringify([...newSet]));
        return newSet;
      });
    } catch (error) {
      console.error("Failed to fetch calendar events:", error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const cachedEvents = localStorage.getItem("calendarEvents");
      const cachedTimestamp = localStorage.getItem("calendarEvents_timestamp");
      const cachedFetchedMonths = localStorage.getItem("fetchedMonths");

      let isCacheValid = false;
      let initialFetchedMonths = new Set();

      if (
        cachedTimestamp &&
        Date.now() - parseInt(cachedTimestamp) < CACHE_DURATION
      ) {
        isCacheValid = true;
      }

      if (isCacheValid && cachedEvents) {
        try {
          const parsedEvents = JSON.parse(cachedEvents).map((event) => ({
            ...event,
            start: new Date(event.start),
            end: new Date(event.end),
          }));
          setEvents(parsedEvents);

          if (cachedFetchedMonths) {
            initialFetchedMonths = new Set(JSON.parse(cachedFetchedMonths));
            setFetchedMonths(initialFetchedMonths);
          }
        } catch (e) {
          console.error("Error parsing cached events", e);
        }
      } else {
        localStorage.removeItem("calendarEvents");
        localStorage.removeItem("calendarEvents_timestamp");
        localStorage.removeItem("fetchedMonths");
      }

      await checkAndFetchMonths(new Date(), initialFetchedMonths);
    };

    initialize();
  }, []);

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
    checkAndFetchMonths(newDate, fetchedMonths);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  // State for handling the modal
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);

  useEffect(() => {
    const loadUsers = async () => {
      // Try to load from local storage first
      const cachedUsers = localStorage.getItem("availableUsers");
      if (cachedUsers) {
        setAvailableUsers(JSON.parse(cachedUsers));
      }

      try {
        const response = await usersAPI.listUsers();
        const users = response.data;

        // Update state and local storage
        setAvailableUsers(users);
        localStorage.setItem("availableUsers", JSON.stringify(users));
      } catch (error) {
        console.error("Failed to load users for suggestions:", error);
      }
    };

    loadUsers();
  }, []);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start: new Date(),
    end: new Date(),
    invitees: "", // For "Invite People" feature
    notifications: [{ days: 0, hours: 1, minutes: 0 }],
    color: "#3174ad",
    property_id: null,
    tenant_id: null,
    artisan_id: null,
  });

  // Triggered when selecting a time slot (creating new)
  const handleSelectSlot = ({ start, end }) => {
    setSelectedEvent(null);
    const now = new Date();
    const startTime = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      now.getHours(),
      0,
      0,
    );
    const endTime = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      now.getHours() + 1,
      0,
      0,
    );
    setFormData({
      title: "",
      description: "",
      start: startTime,
      end: endTime,
      invitees: "",
      notifications: [{ days: 0, hours: 1, minutes: 0 }],
      color: "#3174ad",
      property_id: null,
      tenant_id: null,
      artisan_id: null,
    });
    setOpen(true);
  };

  // Triggered when clicking an existing event (editing)
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      start: event.start,
      end: event.end,
      invitees:
        event.attendees && Array.isArray(event.attendees)
          ? event.attendees.map((a) => a.username || a.email).join(", ")
          : event.invitees || "",
      notifications: event.notifications || [{ days: 0, hours: 1, minutes: 0 }],
      task_id: event.task_id,
      color: event.color || "#3174ad",
      property_id: event.property_id || null,
      tenant_id: event.tenant_id || null,
      artisan_id: event.artisan_id || null,
    });
    setOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventId = params.get("event");

    if (eventId) {
      const fetchAndOpenEvent = async () => {
        try {
          const response = await calendarAPI.getCalendarEvent(eventId);
          const eventData = response.data;
          const formattedEvent = {
            ...eventData,
            start: new Date(eventData.start_time),
            end: new Date(eventData.end_time),
            allDay: eventData.all_day,
          };

          handleSelectEvent(formattedEvent);
          // Also set the current date so the calendar shows the event (context)
          setCurrentDate(formattedEvent.start);

          // Clean up the URL so that on reload/nav we don't reopen it if not desired,
          // or keep it. Keeping it allows sharing links.
        } catch (error) {
          console.error("Failed to fetch event from URL param:", error);
        }
      };

      fetchAndOpenEvent();
    }
  }, [location.search]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async (submittedData) => {
    setLoading(true);
    try {
      // Resolve emails from usernames
      const inviteeNames = submittedData.invitees
        ? submittedData.invitees
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
        : [];

      const attendeeEmails = inviteeNames.map((name) => {
        const user = availableUsers.find(
          (u) => u.username === name || u.email === name,
        );
        return user ? user.email : name;
      });

      // Prepare payload matching backend schema
      const payload = {
        title: submittedData.title,
        description: submittedData.description,
        start_time: submittedData.start.toISOString(),
        end_time: submittedData.end.toISOString(),
        attendee_emails: attendeeEmails,
        notifications: submittedData.notifications,
        color: submittedData.color,
        property_id: submittedData.property_id,
        tenant_id: submittedData.tenant_id,
        artisan_id: submittedData.artisan_id,
      };

      let updatedEvents;
      if (selectedEvent) {
        // Update existing event
        const response = await calendarAPI.updateCalendarEvent(
          selectedEvent.id,
          payload,
        );
        const updatedEvent = {
          ...response.data,
          start: new Date(response.data.start_time),
          end: new Date(response.data.end_time),
          allDay: response.data.all_day,
        };

        updatedEvents = events.map((e) =>
          e.id === selectedEvent.id ? updatedEvent : e,
        );
      } else {
        // Create new event
        const response = await calendarAPI.createCalendarEvent(payload);
        const newEvent = {
          ...response.data,
          start: new Date(response.data.start_time),
          end: new Date(response.data.end_time),
          allDay: response.data.all_day,
        };
        updatedEvents = [...events, newEvent];
      }

      setEvents(updatedEvents);
      localStorage.setItem("calendarEvents", JSON.stringify(updatedEvents));
      localStorage.setItem("calendarEvents_timestamp", Date.now().toString());
      setOpen(false);
    } catch (error) {
      console.error("Failed to save event:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedEvent) {
      try {
        await calendarAPI.deleteCalendarEvent(selectedEvent.id);
        const updatedEvents = events.filter((e) => e.id !== selectedEvent.id);
        setEvents(updatedEvents);
        localStorage.setItem("calendarEvents", JSON.stringify(updatedEvents));
        localStorage.setItem("calendarEvents_timestamp", Date.now().toString());
        setOpen(false);
      } catch (error) {
        console.error("Failed to delete event:", error);
      }
    }
  };

  const handleRefresh = async () => {
    localStorage.removeItem("calendarEvents");
    localStorage.removeItem("calendarEvents_timestamp");
    localStorage.removeItem("fetchedMonths");
    setFetchedMonths(new Set());
    setEvents([]);
    await checkAndFetchMonths(currentDate, new Set());
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Kalender</Typography>
              <MDButton variant="gradient" color="info" onClick={handleRefresh}>
                Aktualisieren
              </MDButton>
            </MDBox>
            <style>
              {`
                .rbc-day-bg:hover {
                  background-color: rgba(73, 163, 241, 0.15) !important;
                  box-shadow: inset 0 0 10px rgba(73, 163, 241, 0.3);
                  cursor: pointer;
                  transition: all 0.2s ease;
                }
              `}
            </style>
            <Calendar
              selectable
              localizer={localizer}
              events={events}
              date={currentDate}
              view={view}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              views={["month", "week", "day", "agenda"]}
              step={60}
              showMultiDayTimes
              startAccessor="start"
              endAccessor="end"
              style={{ height: "75vh" }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              messages={messages}
              formats={formats}
              culture="de"
              eventPropGetter={(event) => ({
                style: {
                  backgroundColor: event.color || "#3174ad", // Default color if not provided
                },
              })}
            />
          </MDBox>
        </Card>
      </MDBox>

      {/* Create/Edit Event Dialog */}
      <EventDialog
        open={open}
        onClose={handleClose}
        onSave={handleSave}
        onDelete={selectedEvent ? handleDelete : null}
        initialData={formData}
        isEditMode={!!selectedEvent}
        availableUsernames={availableUsers
          .map((u) => u.username)
          .filter(Boolean)}
      />
    </DashboardLayout>
  );
};

export default CalendarPage;
